import { LocaleStrings } from '../Locale/localeConstants'
import { DictionaryFormatters } from './DictionaryFormatters'
import { DictionariesByLocale, DictionaryFormatter, DictionaryModel, ReplacerObject } from './DictionaryTypes'

/** Replaces params in a label by a key-value indexed replacer-object */
export const replace = (label: string, replacer: ReplacerObject): string => {
    let translation: string = label
    if (replacer) {
        Object.entries(replacer).forEach(([param, value]) => {
            translation = translation.replace(`{{ ${param} }}`, value?.toString() ?? '')
        })
    }
    return translation
}

/** Gets a dictionary-value by dictionary-key.
 * When a replacer is provided it replaces those params in the found dictionary-value.
 * When no dictionary-value is found it returns the dictionary-key */
const getText = <T = undefined>(
    dictionary: DictionaryModel,
    key: string,
    replacer?: ReplacerObject,
    /** Can be anything except "undefined" */
    fallbackReturnValue?: T,
): string | T => {
    const matchingDictionaryItem = dictionary?.[key]
    if (!matchingDictionaryItem) {
        return typeof fallbackReturnValue !== 'undefined' ? fallbackReturnValue : key
    }

    if (!replacer) return matchingDictionaryItem

    return replace(matchingDictionaryItem, replacer)
}

export const DICTIONARY_TAGS_FOR_REVALIDATION = ['dictionary']
/** Fetches the dictionary from the provided URL.
 * A formatter can be provided to formated the retreived dictionary to the required format.
 * By default a formatter is used that formats from umbraco to the required format
 *
 */
const fetchDictionary = async (
    url: string,
    formatter: DictionaryFormatter = DictionaryFormatters.fromUmbraco,
    fetchOptions: Partial<RequestInit> = {},
): Promise<DictionaryModel> => {
    if (!url) return {}
    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        // @ts-ignore - Required for NextJS caching, cannot do any harm when present in non-NextJS applications
        next: {
            // cache: 'force-cache' /* force cache so caching also applies to dynamic routes */,
            revalidate: 7200 /* caches for 2 hours*/,
            //@ts-ignore
            tags: DICTIONARY_TAGS_FOR_REVALIDATION /* tag that can be used with the revalidateTag function in server components */,
        },
        ...fetchOptions,
    })
    const jsonResponse = await response.json()
    const dictionary: DictionaryModel = formatter(jsonResponse?.data)
    return dictionary
}

/** Fetches multiple language dictionaries from the provided URL
 * A formatter can be provided to formated the retreived dictionary to the required format.
 * By default a formatter is used that formats from umbraco to the required format
 */
const fetchMultipleDictionaries = async (
    url: string,
    formatter: DictionaryFormatter = DictionaryFormatters.fromUmbraco,
): Promise<DictionariesByLocale> => {
    const locales = Object.values(LocaleStrings)
    const dictionariesByLocale: DictionariesByLocale = {}

    const fetches = locales.map((localeString) => {
        const call = fetchDictionary(`${url}&locale=${localeString}`, formatter)
            .then((response) => {
                if (response) dictionariesByLocale[localeString] = response
            })
            .catch((error) => console.error(error))

        return call
    })

    //Wait until all dictionary fetches have finished
    await Promise.all(fetches).catch(() => {
        return dictionariesByLocale
    })

    return dictionariesByLocale
}

/** Function that combines fetchDictionary and "getText" */
const fetchDictionaryAndGetText = async <T = undefined>(
    url: string,
    key: string,
    replacer?: ReplacerObject,
    fallbackReturnValue?: T,
): Promise<string | T> => {
    const dictionary = await fetchDictionary(url)
    return getText(dictionary, key, replacer, fallbackReturnValue)
}

/** Function that filters the dictionary by a matcher (string) */
const filterDictionary = (dictionary: DictionaryModel, mainKey: string): DictionaryModel => {
    const filteredDictionary: DictionaryModel = {}
    const cutoff: number = mainKey.endsWith('.') ? mainKey.length : mainKey.length + 1
    Object.entries(dictionary).forEach(([key, value]) => {
        if (key.startsWith(mainKey)) filteredDictionary[key.slice(cutoff)] = value
    })
    return filteredDictionary
}

/** Object containing everything needed to handle translations in an application */
export const Dictionary = {
    getText,
    fetchDictionaryAndGetText,
    fetchDictionary,
    fetchMultipleDictionaries,
    filterDictionary,
}
